import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaPlus, FaDownload, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { PhotoAlbumWithItems, Photo } from '../../../../types/photoAlbum';
import photoAlbumService from '../../../../services/photoAlbumService';
import Button from '../../../../components/misc/Button';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import PhotoViewerModal from '../../../../components/modals/PhotoViewerModal';
import AddPhotosModal from '../../../../components/modals/AddPhotosModal';
import AlbumSponsorItem from '../../../../components/misc/AlbumSponsorItem';
import sponsorService from '../../../../services/sponsorService';
import Sponsor from '../../../../types/sponsor';
import { buildAlbumGridRows } from '../../../../utils/albumGrid';

/** Matches Tailwind breakpoints used by the album grid. */
function useAlbumColumns(): number {
  const [columns, setColumns] = useState(2);

  useEffect(() => {
    const update = () => {
      const width = window.innerWidth;
      if (width >= 1280) setColumns(5);
      else if (width >= 1024) setColumns(4);
      else if (width >= 640) setColumns(3);
      else setColumns(2);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return columns;
}

export default function CommunityAlbumDetailPage() {
  const { t } = useTranslation();
  const { albumId } = useParams<{ albumId: string }>();
  const community = useSelectedCommunity();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [album, setAlbum] = useState<PhotoAlbumWithItems | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addPhotosModalOpen, setAddPhotosModalOpen] = useState(false);
  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const trackedImpressions = useRef<Set<number>>(new Set());

  const communityId = community?.id;

  useEffect(() => {
    if (!communityId || !albumId || hasFetched) return;

    setLoading(true);
    setHasFetched(true);

    photoAlbumService
      .getPhotoAlbumById(communityId, albumId, 0, 100)
      .then((data) => setAlbum(data))
      .catch((error) => {
        console.error('Failed to load album:', error);
        toast.error(t('error.loadFailed'));
      })
      .finally(() => setLoading(false));
  }, [communityId, albumId]);

  useEffect(() => {
    if (!communityId || !albumId) {
      setSponsors([]);
      return undefined;
    }

    let cancelled = false;
    sponsorService
      .getCommunitySponsors(communityId, { photoAlbumId: albumId })
      .then((data) => {
        if (!cancelled) setSponsors(data);
      })
      .catch(() => {
        if (!cancelled) setSponsors([]);
      });

    return () => {
      cancelled = true;
    };
  }, [communityId, albumId]);

  useEffect(() => {
    if (!communityId || sponsors.length === 0) return;

    sponsors.forEach((sponsor) => {
      if (trackedImpressions.current.has(sponsor.sponsorId)) return;
      trackedImpressions.current.add(sponsor.sponsorId);
      sponsorService.trackImpression(communityId, sponsor.sponsorId).catch(() => {
        /* fire-and-forget */
      });
    });
  }, [sponsors, communityId]);

  const columns = useAlbumColumns();

  const gridRows = useMemo(
    () => buildAlbumGridRows(album?.items ?? [], sponsors, columns),
    [album?.items, sponsors, columns],
  );

  const handleSponsorClick = (sponsor: Sponsor) => {
    if (!communityId) return;
    sponsorService.trackClick(communityId, sponsor.sponsorId).catch(() => {
      /* fire-and-forget */
    });
    navigate(`/community/sponsors/${sponsor.sponsorId}`, { state: { sponsor } });
  };

  const handleUploadPhotos = async (files: File[]): Promise<Photo[]> => {
    if (!community?.id || !albumId) return [];

    const newPhotos = await photoAlbumService.addPhotosToAlbum(community.id, albumId, files);

    if (album) {
      setAlbum({
        ...album,
        items: [...album.items, ...newPhotos],
        photoCount: album.photoCount + newPhotos.length,
      });
    }

    toast.success(t('page.dashboard.albums.uploadSuccess'));
    return newPhotos;
  };

  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
    setIsModalOpen(true);
  };

  const handleDeletePhoto = async (photoId: number) => {
    if (!community?.id || !albumId) return;

    try {
      await photoAlbumService.deletePhoto(community.id, albumId, photoId);

      if (album) {
        setAlbum({
          ...album,
          items: album.items.filter((photo) => photo.id !== photoId),
          photoCount: album.photoCount - 1,
        });
      }

      toast.success(t('page.dashboard.albums.deleteSuccess'));
    } catch (error) {
      console.error('Failed to delete photo:', error);
      if (!navigator.onLine) {
        toast.error(t('error.offline'));
      } else {
        toast.error(t('error.deleteFailed'));
      }
    }
  };

  const handleDownloadAlbum = async () => {
    if (!community?.id || !albumId) return;

    try {
      setIsPreparingDownload(true);
      toast.info(t('page.dashboard.albums.preparingDownload') || 'Preparing download');

      const blob = await photoAlbumService.getPhotoAlbumZip(community.id, albumId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${album?.title || 'album'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('page.dashboard.albums.downloadSuccess'));
    } catch (error) {
      console.error('Failed to download album:', error);
      if (!navigator.onLine) {
        toast.error(t('error.offline'));
      } else {
        toast.error(t('error.downloadFailed'));
      }
    } finally {
      setIsPreparingDownload(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-secondary-600 text-lg">{t('page.dashboard.albums.loading')}</div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-secondary-600 text-lg">{t('page.dashboard.albums.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="primary"
        onClick={() => navigate('/community/albums')}
        className="items-center justify-center gap-2 sm:justify-start"
        aria-label={t('general.back')}>
        <FaArrowLeft />
        <span>{t('general.back')}</span>
      </Button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="truncate text-3xl font-bold text-primary">{album.title}</h1>
          {album.description && <p className="text-secondary-600 mt-2 line-clamp-2">{album.description}</p>}
          <p className="text-secondary-500 mt-2">
            {album.photoCount} {album.photoCount === 1 ? t('general.photo_one') : t('general.photo_other')}
          </p>
        </div>

        <div className="flex w-full gap-2 sm:w-auto">
          <Button
            variant="secondary"
            onClick={handleDownloadAlbum}
            disabled={isPreparingDownload}
            className="flex-1 items-center justify-center gap-2 sm:flex-initial">
            {isPreparingDownload ? <FaSpinner className="h-4 w-4 animate-spin" /> : <FaDownload />}
            <span className="truncate">{t('page.dashboard.albums.download')}</span>
          </Button>
          {album.openToUpload && (
            <Button
              variant="primary"
              onClick={() => setAddPhotosModalOpen(true)}
              className="flex-1 items-center justify-center gap-2 sm:flex-initial">
              <FaPlus />
              <span className="truncate">{t('page.dashboard.albums.upload')}</span>
            </Button>
          )}
        </div>
      </div>

      {album.items.length === 0 && sponsors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-secondary-600 text-lg">{t('page.dashboard.albums.noPhotos')}</p>
          {album.openToUpload && (
            <Button variant="primary" onClick={() => setAddPhotosModalOpen(true)} className="mt-4">
              <FaPlus />
              {t('page.dashboard.albums.uploadFirst')}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {gridRows.map((row) => {
            const hasPhoto = row.cells.some((cell) => cell.kind === 'photo');
            return (
              <div
                key={row.key}
                className="grid gap-4"
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {row.cells.map((item) =>
                  item.kind === 'sponsor' ? (
                    <div
                      key={`sponsor-${item.sponsor.sponsorId}`}
                      className={
                        hasPhoto ? 'col-span-2 h-full min-h-0 min-w-0 self-stretch' : 'col-span-2 aspect-[2/1] min-w-0'
                      }>
                      <AlbumSponsorItem sponsor={item.sponsor} onClick={() => handleSponsorClick(item.sponsor)} />
                    </div>
                  ) : (
                    <div
                      key={item.photo.id}
                      onClick={() => handlePhotoClick(item.photo)}
                      className="group relative aspect-square min-w-0 cursor-pointer overflow-hidden rounded-lg bg-secondary-50 transition-all hover:scale-[1.02] hover:shadow-lg">
                      <img
                        src={item.photo.pictureUrl}
                        alt={`Album item ${item.photo.id}`}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                  ),
                )}
              </div>
            );
          })}
        </div>
      )}

      {album.items.length === 0 && sponsors.length > 0 && (
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-secondary-600 text-lg">{t('page.dashboard.albums.noPhotos')}</p>
          {album.openToUpload && (
            <Button variant="primary" onClick={() => setAddPhotosModalOpen(true)} className="mt-4">
              <FaPlus />
              {t('page.dashboard.albums.uploadFirst')}
            </Button>
          )}
        </div>
      )}

      <PhotoViewerModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        photo={selectedPhoto}
        photos={album.items}
        onDelete={handleDeletePhoto}
        onNavigate={(photo) => setSelectedPhoto(photo)}
      />

      <AddPhotosModal open={addPhotosModalOpen} setOpen={setAddPhotosModalOpen} onUpload={handleUploadPhotos} />
    </div>
  );
}
